import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyName, SurveyNameDTO } from "../types/surveyName";
/* eslint-disable import/export */
export abstract class CreateNewSurveyRPC implements
  LeoRPC<CreateNewSurveyRPC.Request, CreateNewSurveyRPC.Response, CreateNewSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: CreateNewSurveyRPC.Request
  ): Promise<LeoRPCResult<CreateNewSurveyRPC.Response, CreateNewSurveyRPC.Errors.Errors>>;
}

export namespace CreateNewSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyName: SurveyNameDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCreateNewSurveyRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyName: SurveyName;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyName: SurveyName
    ) {
      this.projectId = projectId;
      this.surveyName = surveyName;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyName = SurveyName.fromDTO(getObject(dto, "surveyName", InvalidRequestSurveyNameError));
      return new Request(
        projectId,
        surveyName
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyName: this.surveyName.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CreateNewSurveyRPC(projectId='${this.projectId}',surveyName='${this.surveyName}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isCreateNewSurveyResponse: boolean = true;
  
    readonly surveyId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID
    ) {
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidResponseSurveyIdError);
      return new Response(
        surveyId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyId: this.surveyId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `CreateNewSurvey(surveyId='${this.surveyId}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SurveyNameNotUnique = "SURVEY_NAME_NOT_UNIQUE",
    MaximumSurveyCountExceeded = "MAXIMUM_SURVEY_COUNT_EXCEEDED",
    InvalidSurveyName = "INVALID_SURVEY_NAME"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `CreateNewSurveyRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `CreateNewSurveyRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SurveyNameNotUniqueDTO {
      code: string;
    }
    
    export class SurveyNameNotUnique extends LeoRPCError<RPCError> {
      readonly isSurveyNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNameNotUnique;
        super(code);
      }
    
    
      toDTO(): SurveyNameNotUniqueDTO {
        const dto: SurveyNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNameNotUnique.prototype.toString = function toString(): string {
      return `CreateNewSurveyRPC.SURVEY_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface MaximumSurveyCountExceededDTO {
      code: string;
    }
    
    export class MaximumSurveyCountExceeded extends LeoRPCError<RPCError> {
      readonly isMaximumSurveyCountExceeded: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumSurveyCountExceeded;
        super(code);
      }
    
    
      toDTO(): MaximumSurveyCountExceededDTO {
        const dto: MaximumSurveyCountExceededDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumSurveyCountExceeded.prototype.toString = function toString(): string {
      return `CreateNewSurveyRPC.MAXIMUM_SURVEY_COUNT_EXCEEDED()`;
    };
    
    
    export interface InvalidSurveyNameDTO {
      code: string;
    }
    
    export class InvalidSurveyName extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyName;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyNameDTO {
        const dto: InvalidSurveyNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyName.prototype.toString = function toString(): string {
      return `CreateNewSurveyRPC.INVALID_SURVEY_NAME()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | SurveyNameNotUnique | MaximumSurveyCountExceeded | InvalidSurveyName;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | SurveyNameNotUniqueDTO | MaximumSurveyCountExceededDTO | InvalidSurveyNameDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SurveyNameNotUnique:
          return new SurveyNameNotUnique();
        case RPCError.MaximumSurveyCountExceeded:
          return new MaximumSurveyCountExceeded();
        case RPCError.InvalidSurveyName:
          return new InvalidSurveyName();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends CreateNewSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyNameError extends CreateNewSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyNameError";
  }
}

export class InvalidResponseSurveyIdError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSurveyNameNotUniqueError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNameNotUniqueError";
  }
}

export class InvalidResponseMaximumSurveyCountExceededError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumSurveyCountExceededError";
  }
}

export class InvalidResponseInvalidSurveyNameError extends CreateNewSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyNameError";
  }
}

